import type { Action } from 'redux';

import normaliseError, { type NormaliseErrorInput } from '/shared/functions/normaliseError';
import type { NormalisedError, VehicleType } from '/index';

interface LocationCurrencyOption {
  code: string;
}

interface LocationCountry {
  country_code: string;
  name_english: string;
  default_distance_unit: string;
  language_default: string;
  enabled: boolean;
  carbon_credit_services_enabled?: boolean;
  vehicleTypes?: VehicleType;
  currency: {
    b2cPayment: string;
    b2cDisplay: string;
    cmpSuggest: LocationCurrencyOption | null;
    cmpFiltered: LocationCurrencyOption[];
  };
}

interface SavedLocation {
  countryCode: string;
  name: string;
}

export interface LocationState {
  fetching: boolean;
  fetched: boolean;
  saving: boolean;
  saved: boolean;
  error: NormalisedError | null;
  countries: Array<LocationCountry> | null;
  current: LocationCountry | SavedLocation | null;
}

interface GetCountriesStartedAction extends Action {
  type: 'GET_COUNTRIES_STARTED';
}

interface GetCountriesFulfilledAction extends Action {
  type: 'GET_COUNTRIES_FULFILLED';
  countries: Array<LocationCountry>;
}

interface GetCountriesRejectedAction extends Action {
  type: 'GET_COUNTRIES_REJECTED';
  error: NormalisedError;
}

interface GetCountryStartedAction extends Action {
  type: 'GET_COUNTRY_STARTED';
}

interface GetCountryFulfilledAction extends Action {
  type: 'GET_COUNTRY_FULFILLED';
  country: LocationCountry;
}

interface GetCountryRejectedAction extends Action {
  type: 'GET_COUNTRY_REJECTED';
  error: NormalisedError;
}

interface SaveLocationStartedAction extends Action {
  type: 'SAVE_LOCATION_STARTED';
}

interface SaveLocationFulfilledAction extends Action {
  type: 'SAVE_LOCATION_FULFILLED';
  countryCode: string;
  name: string;
}

interface SaveLocationRejectedAction extends Action {
  type: 'SAVE_LOCATION_REJECTED';
  error: NormaliseErrorInput;
}

interface ClearLocationAction extends Action {
  type: 'CLEAR_LOCATION';
}

type LocationAction =
  | GetCountriesStartedAction
  | GetCountriesFulfilledAction
  | GetCountriesRejectedAction
  | GetCountryStartedAction
  | GetCountryFulfilledAction
  | GetCountryRejectedAction
  | SaveLocationStartedAction
  | SaveLocationFulfilledAction
  | SaveLocationRejectedAction
  | ClearLocationAction;

const defaultState: LocationState = {
  fetching: false,
  fetched: false,
  saving: false,
  saved: true,
  error: null,
  countries: null,
  current: null,
};

function reducer(state: LocationState = defaultState, action: LocationAction): LocationState {
  switch (action.type) {
    case 'GET_COUNTRIES_STARTED': {
      return {
        ...state,
        fetching: true,
        error: null,
      };
    }
    case 'GET_COUNTRIES_FULFILLED': {
      return {
        ...state,
        fetching: false,
        error: null,
        countries: action.countries,
      };
    }
    case 'GET_COUNTRIES_REJECTED': {
      return {
        ...state,
        fetching: false,
        error: action.error,
      };
    }
    case 'GET_COUNTRY_STARTED': {
      return {
        ...state,
        fetching: true,
        error: null,
      };
    }
    case 'GET_COUNTRY_FULFILLED': {
      return {
        ...state,
        fetching: false,
        error: null,
        current: action.country,
      };
    }
    case 'GET_COUNTRY_REJECTED': {
      return {
        ...state,
        fetching: false,
        error: action.error,
      };
    }
    case 'SAVE_LOCATION_STARTED': {
      return {
        ...state,
        saving: true,
        error: null,
      };
    }
    case 'SAVE_LOCATION_FULFILLED': {
      return {
        ...state,
        saving: false,
        saved: true,
        current: {
          countryCode: action.countryCode,
          name: action.name,
        },
      };
    }
    case 'SAVE_LOCATION_REJECTED': {
      return {
        ...state,
        saving: false,
        saved: false,
        error: normaliseError(action.error),
      };
    }
    case 'CLEAR_LOCATION': {
      return { ...defaultState };
    }
    default: {
      return state;
    }
  }
}

export default reducer;
